import { ScratchpadItem } from './ScratchpadItem.js';
import { ChecklistItem } from './ChecklistItem.js';
import { ExpenseItem } from './ExpenseItem.js';

export const DEFAULT_CHECKLIST_PRESETS = [
  { id: 'pre_passport', text: 'chkPassport', defaultText: '여권 및 신분증 지참', category: 'preset' },
  { id: 'pre_esim', text: 'chkEsim', defaultText: '5G eSIM 또는 USIM 활성화', category: 'preset' },
  { id: 'pre_luggage', text: 'chkLuggage', defaultText: '관광안내소/역사 짐보관 위치 확인', category: 'preset' },
  { id: 'pre_transit', text: 'chkTransit', defaultText: '교통카드(티머니/와우패스) 준비', category: 'preset' },
  { id: 'pre_adapter', text: 'chkAdapter', defaultText: '돼지코 변환 플러그(220V)', category: 'preset' },
  { id: 'pre_hotline', text: 'chkHotline', defaultText: '1330 관광통역전화 단축번호 저장', category: 'preset' }
];

/**
 * Domain Aggregate Root: TravelScratchpad
 * Encapsulates the entire travel note, itinerary, checklist, and expense logic
 */
export class TravelScratchpad {
  constructor({
    items = [],
    checklists = [],
    expenses = [],
    updatedAt = new Date().toISOString()
  } = {}) {
    this.items = items;
    this.checklists = checklists.length > 0 ? checklists : this._createDefaultChecklists();
    this.expenses = expenses;
    this.updatedAt = updatedAt;
  }

  _createDefaultChecklists() {
    return DEFAULT_CHECKLIST_PRESETS.map(preset => new ChecklistItem({
      id: preset.id,
      text: preset.defaultText,
      completed: false,
      category: 'preset'
    }));
  }

  // --- Itinerary / Spots ---
  hasSpot(centerId) {
    return this.items.some(item => item.centerId === centerId);
  }

  getSpot(centerId) {
    return this.items.find(item => item.centerId === centerId);
  }

  addSpot(centerId, customNote = '') {
    if (this.hasSpot(centerId)) {
      const existing = this.getSpot(centerId);
      if (customNote) existing.updateNote(customNote);
      this.touch();
      return existing;
    }

    const maxOrder = this.items.reduce((max, it) => Math.max(max, it.order), 0);
    const newItem = new ScratchpadItem({
      centerId,
      customNote,
      order: maxOrder + 1,
      isVisited: false
    });
    this.items.push(newItem);
    this.touch();
    return newItem;
  }

  removeSpot(centerId) {
    this.items = this.items.filter(item => item.centerId !== centerId);
    this.normalizeOrders();
    this.touch();
  }

  toggleVisited(centerId) {
    const item = this.getSpot(centerId);
    if (item) {
      item.toggleVisited();
      this.touch();
      return item.isVisited;
    }
    return false;
  }

  updateSpotNote(centerId, note) {
    const item = this.getSpot(centerId);
    if (item) {
      item.updateNote(note);
      this.touch();
    }
  }

  moveSpot(centerId, direction) { // direction: 'up' | 'down'
    const index = this.items.findIndex(it => it.centerId === centerId);
    if (index === -1) return;

    if (direction === 'up' && index > 0) {
      const temp = this.items[index - 1];
      this.items[index - 1] = this.items[index];
      this.items[index] = temp;
    } else if (direction === 'down' && index < this.items.length - 1) {
      const temp = this.items[index + 1];
      this.items[index + 1] = this.items[index];
      this.items[index] = temp;
    }
    this.normalizeOrders();
    this.touch();
  }

  normalizeOrders() {
    this.items.forEach((item, idx) => {
      item.setOrder(idx + 1);
    });
  }

  // --- Checklist ---
  addChecklist(text) {
    if (!text || !text.trim()) return null;
    const item = new ChecklistItem({
      text: text.trim(),
      completed: false,
      category: 'general'
    });
    this.checklists.push(item);
    this.touch();
    return item;
  }

  toggleChecklist(id) {
    const item = this.checklists.find(c => c.id === id);
    if (item) {
      item.toggle();
      this.touch();
      return item.completed;
    }
    return false;
  }

  removeChecklist(id) {
    this.checklists = this.checklists.filter(c => c.id !== id);
    this.touch();
  }

  resetChecklistPresets(tFunc) {
    this.checklists = DEFAULT_CHECKLIST_PRESETS.map(preset => new ChecklistItem({
      id: preset.id,
      text: tFunc ? tFunc(preset.text) || preset.defaultText : preset.defaultText,
      completed: false,
      category: 'preset'
    }));
    this.touch();
  }

  // --- Expenses ---
  addExpense(title, amount, category = 'food') {
    const item = new ExpenseItem({
      title,
      amount,
      category
    });
    this.expenses.push(item);
    this.touch();
    return item;
  }

  removeExpense(id) {
    this.expenses = this.expenses.filter(e => e.id !== id);
    this.touch();
  }

  getTotalExpense() {
    return this.expenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
  }

  touch() {
    this.updatedAt = new Date().toISOString();
  }

  // --- Serialization ---
  toJSON() {
    return {
      items: this.items.map(it => it.toJSON()),
      checklists: this.checklists.map(c => c.toJSON()),
      expenses: this.expenses.map(e => e.toJSON()),
      updatedAt: this.updatedAt
    };
  }

  static fromJSON(json) {
    if (!json) return new TravelScratchpad();
    const items = (json.items || []).map(it => ScratchpadItem.fromJSON(it)).filter(Boolean);
    const checklists = (json.checklists || []).map(c => ChecklistItem.fromJSON(c)).filter(Boolean);
    const expenses = (json.expenses || []).map(e => ExpenseItem.fromJSON(e)).filter(Boolean);
    return new TravelScratchpad({
      items,
      checklists,
      expenses,
      updatedAt: json.updatedAt || new Date().toISOString()
    });
  }
}
